// React Router 7 route tree for the operator console. B2 shipped the
// first ops screens on top of the Phase 1 scaffold: /tenants (+detail),
// /health, /audit. B3 adds /licenses (+detail). B4 adds /billing
// (plan-override) + /flags (read-only katalog). B5 adds /impersonations
// (oturum kaydı yönetimi) + /exports (gerekçeli dışa aktarım). Plan
// madde 3'ün kalan yüzeyi (/incidents) sonraki batch'te.
// Catch-all redirects to home so a stale /people-style link from the
// customer console never lands the user on a 404 inside internal tooling.
//
// CODE-SPLITTING (perf sweep): the first-paint path — AppShell, home,
// tenants (+detail) and the OIDC callback — stays eagerly imported.
// Infrequently-visited ops screens defer to their own chunks via lazyRoute.
// Pages keep their normal named exports in src/routes/* — ONLY the router
// import is dynamic, so the per-page tests are unaffected.

import type { ComponentType } from "react";

import { createBrowserRouter, Navigate } from "react-router";

import { AppShell } from "@/components/AppShell";
import { RequireOperatorAuth } from "@/auth/RequireOperatorAuth";
import {
  RouteErrorBoundary,
  RouteHydrateFallback,
} from "@/components/route-boundaries";
import { CallbackPage } from "@/routes/callback";
import { HomePage } from "@/routes/home";
import { TenantDetailPage } from "@/routes/tenant-detail";
import { TenantsPage } from "@/routes/tenants";

/**
 * Defers a route Component to its own chunk (Vite splits on the dynamic
 * import). Every lazy route gets the shared skeleton fallback for the
 * initial-load case and a bounded retryable error boundary INSIDE the
 * shell (chunk-fetch failure or render crash never blanks the page).
 */
function lazyRoute<M>(load: () => Promise<M>, pick: (mod: M) => ComponentType) {
  return {
    lazy: async () => ({ Component: pick(await load()) }),
    HydrateFallback: RouteHydrateFallback,
    ErrorBoundary: RouteErrorBoundary,
  };
}

export const router = createBrowserRouter([
  // OIDC callback must remain unguarded: it consumes the authorization-code
  // redirect before an operator session exists.
  { path: "/callback", Component: CallbackPage },
  {
    path: "/",
    element: (
      <RequireOperatorAuth>
        <AppShell />
      </RequireOperatorAuth>
    ),
    children: [
      { index: true, Component: HomePage },
      { path: "tenants", Component: TenantsPage },
      { path: "tenants/:id", Component: TenantDetailPage },
      {
        path: "health",
        ...lazyRoute(() => import("@/routes/health"), (m) => m.HealthPage),
      },
      {
        path: "audit",
        ...lazyRoute(() => import("@/routes/audit"), (m) => m.AuditPage),
      },
      {
        path: "impersonations",
        ...lazyRoute(() => import("@/routes/impersonations"), (m) => m.ImpersonationsPage),
      },
      {
        path: "licenses",
        ...lazyRoute(() => import("@/routes/licenses"), (m) => m.LicensesPage),
      },
      {
        path: "licenses/:id",
        ...lazyRoute(() => import("@/routes/license-detail"), (m) => m.LicenseDetailPage),
      },
      {
        path: "billing",
        ...lazyRoute(() => import("@/routes/billing"), (m) => m.BillingPage),
      },
      {
        path: "exports",
        ...lazyRoute(() => import("@/routes/exports"), (m) => m.ExportsPage),
      },
      {
        path: "payroll-parameters",
        ...lazyRoute(
          () => import("@/routes/payroll-parameters"),
          (m) => m.PayrollParametersPage,
        ),
      },
      {
        path: "payroll-parameters/:id",
        ...lazyRoute(
          () => import("@/routes/payroll-parameter-detail"),
          (m) => m.PayrollParameterDetailPage,
        ),
      },
      {
        path: "flags",
        ...lazyRoute(() => import("@/routes/flags"), (m) => m.FlagsPage),
      },
      {
        path: "support-inquiries",
        ...lazyRoute(() => import("@/routes/support-inquiries"), (m) => m.SupportInquiriesPage),
      },
      {
        path: "dlq",
        ...lazyRoute(() => import("@/routes/dlq"), (m) => m.DlqPage),
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
